import { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import {
  CREATE_PAYPAL_CPM_CHECKOUT,
  CREATE_PAYPAL_CPM_UPGRADE_CHECKOUT,
  CREATE_PAYPAL_CPM_ADDON_CHECKOUT,
  type PaypalCpmCheckoutVariables,
  type PaypalCpmAddOnCheckoutVariables,
} from '@scio3/graphql-client';

type CheckoutOut =
  | { status?: boolean; url?: string; message?: string; applied?: boolean }
  | undefined;

/**
 * PayPal (Custom Payment Method) checkout starters — NEW subscription (`startPaypalCheckout`) and
 * license UPGRADE on an existing PayPal sub (`startPaypalUpgrade`, one-time prorated charge since
 * invoices.pay is blocked for CPM). No frontend on/off flag — the backend is the single source of
 * truth (returns `status:false` when not applicable → caller falls back / toasts). On success each
 * full-page-redirects to the Stripe-hosted PayPal flow; the webhook persists state on return.
 */
export function usePaypalCpmCheckout() {
  const [start] = useMutation(CREATE_PAYPAL_CPM_CHECKOUT);
  const [startUpgrade] = useMutation(CREATE_PAYPAL_CPM_UPGRADE_CHECKOUT);
  const [startAddon] = useMutation(CREATE_PAYPAL_CPM_ADDON_CHECKOUT);
  const [loading, setLoading] = useState(false);

  // Shared: redirect to the Stripe-hosted PayPal flow on success, else surface the backend message.
  const redirectOr = useCallback(
    (out: CheckoutOut): { ok: boolean; applied?: boolean; message?: string } => {
      if (out?.status && out.url) {
        window.location.href = out.url;
        return { ok: true };
      }
      // Credit-covered upgrade: the backend applied it directly (no charge, no redirect). Not an error —
      // the caller shows success + refreshes instead of navigating to PayPal.
      if (out?.status && out.applied) {
        return { ok: true, applied: true, message: out.message };
      }
      return { ok: false, message: out?.message || 'Could not start PayPal checkout.' };
    },
    [],
  );

  const startPaypalCheckout = useCallback(
    async (
      params: PaypalCpmCheckoutVariables['params'],
    ): Promise<{ ok: boolean; applied?: boolean; message?: string }> => {
      setLoading(true);
      try {
        const res = await start({ variables: { params } });
        return redirectOr(
          (res.data as { createPaypalCpmCheckout?: CheckoutOut } | undefined)
            ?.createPaypalCpmCheckout,
        );
      } catch (e) {
        return {
          ok: false,
          message: e instanceof Error ? e.message : 'Could not start PayPal checkout.',
        };
      } finally {
        setLoading(false);
      }
    },
    [start, redirectOr],
  );

  const startPaypalUpgrade = useCallback(
    async (
      params: PaypalCpmCheckoutVariables['params'],
    ): Promise<{ ok: boolean; applied?: boolean; message?: string }> => {
      setLoading(true);
      try {
        const res = await startUpgrade({ variables: { params } });
        return redirectOr(
          (res.data as { createPaypalCpmUpgradeCheckout?: CheckoutOut } | undefined)
            ?.createPaypalCpmUpgradeCheckout,
        );
      } catch (e) {
        return {
          ok: false,
          message: e instanceof Error ? e.message : 'Could not start PayPal upgrade.',
        };
      } finally {
        setLoading(false);
      }
    },
    [startUpgrade, redirectOr],
  );

  const startPaypalAddonCheckout = useCallback(
    async (
      params: PaypalCpmAddOnCheckoutVariables['params'],
    ): Promise<{ ok: boolean; applied?: boolean; message?: string }> => {
      setLoading(true);
      try {
        const res = await startAddon({ variables: { params } });
        return redirectOr(
          (res.data as { createPaypalCpmAddOnCheckout?: CheckoutOut } | undefined)
            ?.createPaypalCpmAddOnCheckout,
        );
      } catch (e) {
        return {
          ok: false,
          message: e instanceof Error ? e.message : 'Could not start PayPal add-on checkout.',
        };
      } finally {
        setLoading(false);
      }
    },
    [startAddon, redirectOr],
  );

  return { startPaypalCheckout, startPaypalUpgrade, startPaypalAddonCheckout, loading };
}
